// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once admin JS imports sp apps directly.
// `dasherize` and `escapeAttribute` are local because MiniRacer loads this classic script without module imports.
/* global Handlebars */

(() => {
  const { selectValue, selectValues, selectableValues, selectionValues } = globalThis.AdminSelectChoices;
  // sp-select reads these as on for `true`, `"true"`, the bare attribute or its own name.
  const valueBooleanAttributes = new Set([
    "multiple",
    "searchable",
    "selected-tags",
    "selection-clearable",
    "selectable-branches",
    "search-all-levels",
  ]);
  // uber-select's `data-visibility` values and the sp-select item `visibility` each one names.
  const optionVisibilities = { query: "search", "no-query": "browse" };
  // sp-select reads these as on unless absent, `false` or `"false"`.
  const presenceBooleanAttributes = new Set(["disabled", "required"]);
  // A list with more selectable rows than this gets a search field, as `Admin::SpSelectHelper` gives it one.
  const SELECT_SEARCH_THRESHOLD = 10;

  // Renders an sp-select named `name`, like `Admin::SpSelectHelper#sp_select_tag`: `choices` takes the Rails select
  // choices that helper takes, and sp-select item objects; `selected` names the selected value or values.
  function spSelectTag(
    name,
    {
      choices,
      selected,
      multiple: multipleOption = false,
      includeBlank,
      includeHidden = true,
      testId,
      prompt,
      disabledOptions,
      ...attributes
    } = {},
  ) {
    rejectRetiredOptions(attributes);

    if (attributes.disabled != null && typeof attributes.disabled !== "boolean") {
      disabledOptions ??= attributes.disabled;
      delete attributes.disabled;
    }

    const multiple = isValueOn("multiple", multipleOption);
    // Any given prompt counts, a blank one included, as in Rails' `select`.
    const hasPrompt = prompt !== undefined && prompt !== null && prompt !== false;
    if (!multiple && isPresent(attributes.required) && includeBlank == null && !hasPrompt) {
      includeBlank = true;
    }

    const blankChoice = leadingBlank(includeBlank);
    const allChoices = blankChoice ? [blankChoice, ...choiceList(choices)] : choices;
    const items = disabledChoices(itemsForChoices(allChoices, multiple), disabledOptions);
    const selectionGiven = Object.hasOwn(arguments[1] ?? {}, "selected");
    const defaultSelection = !multiple && !selectionGiven && items !== undefined ? "" : selected;
    const value = withMarkedSelection(items, defaultSelection, attributeSelection(allChoices), multiple);
    const branchesSelectable =
      !attributes.navigation ||
      attributes.navigation === "flat" ||
      isValueOn("selectable-branches", attributes.selectableBranches);
    const choiceOptions = { branchesSelectable, navigation: attributes.navigation };
    const allowCustom = isValueOn("allow-custom", attributes.allowCustom);
    const choiceItems = allowCustom ? withCustomItems(items, value, choiceOptions) : items;
    const unseededPrompt =
      !multiple &&
      hasPrompt &&
      isBlankSelection(selected) &&
      (value == null || !selectableValues(choiceItems ?? [], choiceOptions).includes(String(value)));
    const selectedValue = unseededPrompt ? undefined : selectedChoiceValue(choiceItems, value, multiple, choiceOptions);
    const tagAttributes = { ...attributes };
    const submittedName = multiple && name && !String(name).endsWith("[]") ? `${name}[]` : name;

    if (name) {
      tagAttributes.name = submittedName;

      if (!("id" in attributes)) {
        tagAttributes.id = sanitizeToId(name);
      }
    }

    if (choiceItems !== undefined) {
      tagAttributes.items = JSON.stringify(choiceItems);
    }

    if (choiceItems !== undefined && !("searchable" in attributes)) {
      tagAttributes.searchable = allowCustom || multiple || spSelectSearchable(choiceItems);
    }

    if (selectedValue !== undefined) {
      tagAttributes.value = typeof selectedValue === "boolean" ? String(selectedValue) : selectedValue;
    }

    // A prompt is the placeholder, as `Admin::SpSelectHelper` renders it, never a selectable row.
    if (hasPrompt && !("placeholder" in attributes)) {
      tagAttributes.placeholder = typeof prompt === "string" ? prompt : defaultPrompt();
    }

    if (unseededPrompt && !("prompt" in attributes)) {
      tagAttributes.prompt = typeof prompt === "string" ? prompt : defaultPrompt();
    }

    if (testId !== undefined) {
      tagAttributes["data-testid"] = testId;
    }

    if (multiple) {
      tagAttributes.multiple = true;
      tagAttributes["selected-tags"] = true;
      tagAttributes["selection-clearable"] = true;
    }

    const host = `<sp-select${renderAttributes(tagAttributes)}></sp-select>`;

    if (!multiple || !name || !isSet(includeHidden)) {
      return host;
    }

    return `${blankInput(submittedName, attributes)}${host}`;
  }

  function disabledChoices(items, disabled) {
    if (items === undefined || disabled == null) {
      return items;
    }

    const disabledValues = (Array.isArray(disabled) ? disabled : [disabled]).map(String);

    return items.map(function disableChoice(item) {
      const current = isPlainObject(item) ? item : { title: String(item), value: item };
      const choice = { ...current };

      if (Array.isArray(current.children)) {
        choice.children = disabledChoices(current.children, disabled);
      }

      if ("value" in current && disabledValues.includes(String(current.value))) {
        choice.props = { ...current.props, disabled: true };
      }

      return choice;
    });
  }

  function rejectRetiredOptions(options) {
    for (const [retired, current] of [
      ["items", "choices"],
      ["value", "selected"],
    ]) {
      if (retired in options) {
        throw new Error(`sp_select_tag takes \`${current}\`, not \`${retired}\``);
      }
    }
  }

  // The `helpers.select.prompt` translation, from the runtime config the page and the server renderers provide.
  function defaultPrompt() {
    return globalThis.SparkleConfig?.i18n?.selectPrompt;
  }

  // Counts the rows a user can pick, nested rows included and placeholders left out, as `Admin::SpSelectHelper` does.
  function selectableCount(items) {
    return items.reduce(function countRow(count, item) {
      if (!isPlainObject(item)) {
        return count + 1;
      }

      const own = "value" in item ? 1 : 0;

      return count + own + selectableCount(item.children ?? []);
    }, 0);
  }

  function choiceList(choices) {
    if (choices === undefined || choices === null) {
      return [];
    }

    return Array.isArray(choices) ? choices : Object.entries(choices);
  }

  // The blank row `includeBlank` adds, as `Admin::SpSelect::LeadingOptions` builds it.
  function leadingBlank(includeBlank) {
    if (includeBlank === undefined || includeBlank === null || includeBlank === false) {
      return undefined;
    }

    return typeof includeBlank === "string" ? [includeBlank, ""] : ["", "", { label: " " }];
  }

  // Reads choices as Rails' `options_for_select` does: an object is a list of `label: value` pairs, a `[label, value]`
  // or `[label, value, attributes]` array is one option, a string, number or boolean is its own label and value, and an
  // sp-select item is kept.
  function itemsForChoices(choices, multiple) {
    if (choices === undefined || choices === null) {
      return undefined;
    }

    return choiceList(choices).map((choice, index) => {
      if (isPlainObject(choice)) {
        return choice;
      }

      const item = optionItem(Array.isArray(choice) ? choice : [choice, choice]);

      // A single select's blank first option is its placeholder row.
      if (!multiple && index === 0 && item.value === "") {
        Object.assign(item, { placeholder: true, pendingChoice: false });
      }

      return item;
    });
  }

  function optionItem(option) {
    const attributes = Object.assign({}, ...option.filter(isPlainObject));
    const parts = option.filter((part) => !isPlainObject(part));
    const [label, value] = [parts[0], parts[parts.length - 1]];
    // A non-empty `label` attribute is the title, as in `Admin::SpSelect::ItemsBuilder`.
    const title = attributes.label == null || attributes.label === "" ? label : attributes.label;
    const item = { title: String(title ?? ""), value: String(value ?? ""), dataset: optionDataset(attributes) };

    if (attributes.icon != null) {
      item.icon = String(attributes.icon);
    }

    if (item.dataset.matchValue) {
      item.searchText = item.dataset.matchValue;
    }

    if (item.dataset.subtext) {
      item.subtitle = item.dataset.subtext;
    }

    if (optionVisibilities[item.dataset.visibility]) {
      item.visibility = optionVisibilities[item.dataset.visibility];
    }

    const props = {};

    if (optionAttributePresent(attributes.disabled)) {
      props.disabled = true;
    }

    if (item.dataset.testid !== undefined) {
      props["data-testid"] = item.dataset.testid;
    }

    if (Object.keys(props).length) {
      item.props = props;
    }

    return item;
  }

  // `data: { foo_bar: 1 }` and `"data-foo-bar": 1` both become `dataset.fooBar`, as `Admin::SpSelect::ItemsBuilder` reads them.
  function optionDataset(attributes) {
    const dataset = {};
    const entries = [
      ...Object.entries(isPlainObject(attributes.data) ? attributes.data : {}).map(([key, entry]) => [
        key.replace(/_/g, "-"),
        entry,
      ]),
      ...Object.entries(attributes)
        .filter(([key]) => key.toLowerCase().startsWith("data-"))
        .map(([key, entry]) => [key.slice(5), entry]),
    ];

    for (const [key, entry] of entries) {
      const datasetKey = key.toLowerCase().replace(/-([a-z])/g, (_match, character) => character.toUpperCase());

      if (entry != null && !(datasetKey in dataset)) {
        dataset[datasetKey] = typeof entry === "string" ? entry : JSON.stringify(entry);
      }
    }

    return dataset;
  }

  // Values of options with a `selected` attribute other than `null`, `undefined`, or boolean `false`.
  function attributeSelection(choices) {
    return (Array.isArray(choices) ? choices : [])
      .filter(
        (choice) =>
          Array.isArray(choice) && choice.some((part) => isPlainObject(part) && optionAttributePresent(part.selected)),
      )
      .map((choice) => optionItem(choice).value);
  }

  // Marked options join `selected`, as in `options_for_select`; a single select takes the last
  // selected option in choice order.
  function withMarkedSelection(items, selected, marked, multiple) {
    if (marked.length === 0) {
      return selected;
    }

    const wanted = [...selectionValues(selected).map(String), ...marked];

    if (multiple) {
      return wanted;
    }

    const chosen = items.filter((item) => isPlainObject(item) && wanted.includes(String(item.value)));

    return chosen[chosen.length - 1].value;
  }

  function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function optionAttributePresent(value) {
    return value != null && value !== false;
  }

  function isBlankSelection(value) {
    return (
      value == null ||
      value === false ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0)
    );
  }

  // A cleared multiple sp-select submits no entry, so the blank input keeps the parameter, as Rails does.
  function blankInput(name, { disabled, form }) {
    const inputAttributes = {
      type: "hidden",
      name,
      value: "",
      disabled,
      form,
      autocomplete: "off",
    };

    return `<input${renderAttributes(inputAttributes)}>`;
  }

  // A saved custom value no selectable row holds gets a row of its own, as `Admin::SpSelectHelper` gives it one.
  function withCustomItems(items, value, choiceOptions) {
    if (items === undefined) {
      return items;
    }

    const known = new Set(selectableValues(items, choiceOptions));
    const missing = uniqueValues(selectionValues(value))
      .map(String)
      .filter((entry) => entry !== "" && !known.has(entry));

    return [...items, ...missing.map((entry) => ({ title: entry, value: entry }))];
  }

  // Keeps the first of each value, keyed as `selectValues` keys them.
  function uniqueValues(values) {
    return [...new Map(values.map((value) => [String(value), value])).values()];
  }

  function selectedChoiceValue(items, value, multiple, choiceOptions) {
    if (items === undefined) {
      return multiple && value !== undefined ? JSON.stringify(uniqueValues(selectionValues(value))) : value;
    }

    if (multiple) {
      return JSON.stringify(selectValues(items, value, choiceOptions));
    }

    return selectValue(items, value, choiceOptions);
  }

  function spSelectName(...parts) {
    return parts.join("");
  }

  function renderAttributes(attributes) {
    return Object.entries(attributes)
      .flatMap(([name, value]) => renderAttribute(name, value))
      .join("");
  }

  function renderAttribute(name, value) {
    const attributeName = dasherize(name);

    if (valueBooleanAttributes.has(attributeName)) {
      return isValueOn(attributeName, value) ? [` ${attributeName}`] : [];
    }

    if (presenceBooleanAttributes.has(attributeName)) {
      return isPresent(value) ? [` ${attributeName}`] : [];
    }

    return value === undefined || value === null || value === false
      ? []
      : [` ${attributeName}="${escapeAttribute(value)}"`];
  }

  // Custom-element boolean props read the string "false" as off.
  function isSet(value) {
    return Boolean(value) && value !== "false";
  }

  function isValueOn(name, value) {
    return value === true || value === "true" || value === "" || value === name;
  }

  function isPresent(value) {
    return value !== undefined && value !== null && value !== false && value !== "false";
  }

  // Rails' `sanitize_to_id`: `"user[role_ids][]"` → `"user_role_ids_"`.
  function sanitizeToId(name) {
    return String(name)
      .replaceAll("]", "")
      .replace(/[^-a-zA-Z0-9:.]/gu, "_");
  }

  function dasherize(name) {
    return name.replace(/[A-Z]/g, (character) => `-${character.toLowerCase()}`);
  }

  function escapeAttribute(value) {
    return String(value).replace(
      /[&"'<>]/g,
      (character) =>
        ({
          "&": "&amp;",
          '"': "&quot;",
          "'": "&#39;",
          "<": "&lt;",
          ">": "&gt;",
        })[character],
    );
  }

  /** Returns whether the selectable choices exceed the default search threshold. */
  function spSelectSearchable(items) {
    return selectableCount(items) > SELECT_SEARCH_THRESHOLD;
  }

  Object.assign(globalThis.AdminSelectChoices, {
    spSelectName,
    spSelectTag,
    spSelectSearchable,
  });

  if (globalThis.Handlebars) {
    Handlebars.registerHelper("sp_select_name", (...parts) => spSelectName(...parts.slice(0, -1)));
    Handlebars.registerHelper("sp_select_tag", function spSelectTagHelper(name, options) {
      return new Handlebars.SafeString(spSelectTag(name, options.hash));
    });
  }
})();
